import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import PubSub from 'pubsub-js';

import { SWIPER_MOUNTED } from 'core/site/constants';

import styles from './index.styl';

/**
 * @author Andrei Rashchupkin <a.rashchupkin@rambler-co.ru>
 *
 * @description
 * Компонент отвечающий за базовую реализацию перелистывания элементов
 * с помощью свайпов как на мобильных так и на десктопных девайсах.
 */
export default class Swipe extends PureComponent {
  static propTypes = {
    /**
     * Индекс активного элемента на момент инициализации компонента.
     *
     * Изменение пропса после инициализации компонента не будет иметь
     * какой-либо эффект.
     *
     * Для программного изменения активного элемент после инициализации
     * компонента нужно использовать публичные методы.
     */
    defaultItemIndex: PropTypes.number,

    /**
     * Обработчик изменения прогресса компонента свайпа.
     *
     * В обработчик передается прогресс свайпа принимающий значение
     * от 0 до 1 и общее кол-во слайдов.
     */
    onProgressChange: PropTypes.func,

    /**
     * Обработчик смены активного слайда во время перелистывания элементов.
     *
     * В обработчик передается индекс активного слайда и общее кол-во слайдов.
     */
    onActiveIndexChange: PropTypes.func,

    /**
     * Обработчик смены активного слайда наиближайшего к левому краю активной
     * области. Если ширина слайдов меньше 100%, то это событие даст понять
     * какой слайд является самым левым в видимой области.
     *
     * В обработчик передается индекс активного слайда наиближайшего к левому
     * краю и общее кол-во слайдов.
     */
    onSnapIndexChange: PropTypes.func,

    /**
     * Обработчик пользовательского взаимодействия с компонентом.
     *
     * Будет срабатывать каждый раз, когда пользовать начал какое-то
     * взаимодействие с компонентов будь то свайп, клик или tap.
     */
    onInteraction: PropTypes.func,

    /**
     * Элементы для перелистывания
     */
    children: PropTypes.node,

    /**
     * Отключает встроенное управление translate'ом
     * (https://swiperjs.com/swiper-api#param-virtualTranslate).
     */
    virtualTranslate: PropTypes.bool,

    /**
     * Используемый эффект перелистывания слайдов
     * (https://swiperjs.com/swiper-api#param-effect).
     */
    effect: PropTypes.string,

    /**
     * Длительность перехода между слайдами (в мс)
     */
    speed: PropTypes.number,

    /**
     * Задержка между переходами (в мс).
     * Если этот параметр не задан, автовоспроизведение будет отключено.
     */
    autoplay: PropTypes.number,

    /**
     * Остановка автовоспроизведения ри достижении последнего слайда
     * (не действует в режиме loop)
     */
    autoplayStopOnLast: PropTypes.bool,

    /**
     * При установке в true отключает автовоспроизведение при первом же свайпе
     */
    autoplayDisableOnInteraction: PropTypes.bool,

    /**
     * Доподнительный класс для контейнера
     */
    containerClass: PropTypes.string,

    /**
     * Дополнительный класс для враппера
     */
    wrapperClass: PropTypes.string,

    /**
     * Количество слайдов, видимых в контейнере свайпера
     */
    slidesPerView: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),

    /**
     * Отображение активного слайда по центру
     */
    centeredSlides: PropTypes.bool,

    /**
     * Устанавливает циклическую прокрутку слайдов
     */
    loop: PropTypes.bool,

    /**
     * autoHeight для swiper
     * https://swiperjs.com/swiper-api#param-autoHeight
     */
    autoHeight: PropTypes.bool,

    freeMode: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.object,
    ]),
  };

  static defaultProps = {
    speed: 300,
    slidesPerView: 'auto',
  };

  componentDidMount() {
    if (process.env.BROWSER_RUNTIME) {
      import('swiper').then(module => {
        import('swiper/modules').then(effectModule => {
          const Swiper = module.default;
          const { FreeMode } = effectModule;

          const {
            defaultItemIndex,
            onInteraction,
            onProgressChange,
            onSnapIndexChange,
            onActiveIndexChange,
            virtualTranslate,
            effect,
            speed,
            autoplay,
            autoplayStopOnLast,
            autoplayDisableOnInteraction,
            slidesPerView,
            centeredSlides,
            loop,
            autoHeight,
            freeMode,
          } = this.props;

          this.swiper = new Swiper(this.container, {
            slideClass: styles.slide,
            wrapperClass: styles.wrapper,
            effect,
            speed,
            autoHeight,
            freeMode,
            modules: [FreeMode],

            /**
             * При обновлении потомков свайпер будет запускать процесс обновления
             * своего состояния.
             */
            observer: true,

            /**
             * Размеры слайдов определяются их размерами.
             * Поддерживаются как фиксированный так и процентные значения.
             */
            slidesPerView,

            /**
             * Для каждого слайда высчитывается его прогресс перелистывания.
             * Необходимо для кастомизации поведения.
             */
            watchSlidesProgress: true,

            initialSlide: defaultItemIndex,

            autoplay,
            stopOnLastSlide: autoplayStopOnLast,
            disableOnInteraction: autoplayDisableOnInteraction,
            centeredSlides,
            loop,

            virtualTranslate,

            on: {
              progress(swiper, progress) {
                onProgressChange && onProgressChange({
                  progress,
                  count: swiper.slides.length,
                });
              },

              activeIndexChange(swiper) {
                onActiveIndexChange && onActiveIndexChange({
                  index: swiper.activeIndex,
                  realIndex: swiper.realIndex,
                  count: swiper.slides.length,
                });
              },

              snapIndexChange(swiper) {
                onSnapIndexChange && onSnapIndexChange({
                  index: loop ? swiper.realIndex : swiper.snapIndex,
                  count: swiper.slides.length,
                });
              },

              touchStart() {
                onInteraction && onInteraction();
              },
            },
          });
          PubSub.publish(SWIPER_MOUNTED);
        });
      });
    }
  }

  componentWillUnmount() {
    if (this.swiper) {
      // Удаляем инстанс и подчищаем стили добавленные в документ.
      this.swiper.destroy(true, true);
    }
  }

  /**
   * Реинициализация компонента.
   * @public
   */
  onResize() {
    if (process.env.BROWSER_RUNTIME) {
      this.swiper && this.swiper.update();
    }
  }

  /**
   * Переход на следующий элемент.
   * @public
   */
  next() {
    if (process.env.BROWSER_RUNTIME) {
      this.swiper && this.swiper.slideNext();
    }
  }

  /**
   * Переход на предыдущий элемент.
   * @public
   */
  prev() {
    if (process.env.BROWSER_RUNTIME) {
      this.swiper && this.swiper.slidePrev();
    }
  }

  /**
   * Переход на указанный индекс элемента.
   * @param {number} index
   * @public
   */
  goto(index) {
    if (process.env.BROWSER_RUNTIME) {
      this.swiper && this.swiper.slideTo(index);
    }
  }

  update() {
    if (process.env.BROWSER_RUNTIME) {
      this.swiper && this.swiper.update();
    }
  }

  /**
   * Переданный инстанс `Swipe` будет контролироваться текущем компонентом.
   * @public
   */
  control(instance) {
    if (process.env.BROWSER_RUNTIME && instance) {
      this.swiper.params.control = instance.swiper;
    }
  }

  /**
   * Возвращает имя класса всех слайдов
   * @public
   */
  getSlideClassName() {
    return this.swiper && this.swiper.params.slideClass;
  }

  /**
   * Возвращает DOM-элемент, являющийся контейнером слайдов
   * @public
   */
  getSlidesWrapperElement() {
    return this.swiper && this.swiper.wrapperEl;
  }

  /**
   * Возвращает список элементов слайдера.
   * @public
   */
  getSlides() {
    return this.swiper && this.swiper.slides;
  }

  render() {
    const {
      containerClass,
      wrapperClass,
    } = this.props;
    const slides = []
      .concat(this.props.children)
      .filter(item => item && item.type === Slide);

    return (
      <div
        ref={node => (this.container = node)}
        className={cx(styles.container, containerClass)}
      >
        <div className={cx(styles.wrapper, wrapperClass)}>
          {slides}
        </div>
      </div>
    );
  }
}

export function Slide(params) {
  const { children, width, flexGrow, additionalClass } = params;
  return (
    <div
      className={cx(styles.slide, additionalClass)}
      style={{
        ...width && { width },
        ...flexGrow && { flexGrow },
      }}
    >
      {children}
    </div>
  );
}
